// Broadcast — compose and send breaking-news alerts.
//
// Backend endpoints (admin-only on the API side):
//   POST /news/admin/broadcasts/preview       — estimate recipient count
//   POST /news/admin/notifications/broadcast  — send the broadcast
//   GET  /news/admin/broadcasts               — paginated history
//
// Body fields (must match server zod schema):
//   title, body, alert_type (informational|breaking),
//   scope (app_wide|country_targeted), target_countries (ISO-3166-1 alpha-2),
//   post_id (optional, UUID extracted from a share URL)
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import {
  Input,
  Label,
  Select,
  Textarea,
} from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

type AlertType = "informational" | "breaking";
type Scope = "app_wide" | "country_targeted";

interface BroadcastRow {
  id: string;
  title: string;
  body: string;
  alert_type: AlertType;
  scope: Scope;
  target_countries: string[] | null;
  recipients_count: number | null;
  sent_at: string;
  sent_by: string;
}

const COUNTRIES: { code: string; name: string }[] = [
  { code: "DZ", name: "Algeria" },
  { code: "AO", name: "Angola" },
  { code: "BJ", name: "Benin" },
  { code: "BW", name: "Botswana" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CM", name: "Cameroon" },
  { code: "CV", name: "Cape Verde" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "DR Congo" },
  { code: "DJ", name: "Djibouti" },
  { code: "EG", name: "Egypt" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GH", name: "Ghana" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "KE", name: "Kenya" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "ML", name: "Mali" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "RW", name: "Rwanda" },
  { code: "ST", name: "São Tomé and Príncipe" },
  { code: "SN", name: "Senegal" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "SS", name: "South Sudan" },
  { code: "SD", name: "Sudan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TG", name: "Togo" },
  { code: "TN", name: "Tunisia" },
  { code: "UG", name: "Uganda" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "FR", name: "France" },
  { code: "BR", name: "Brazil" },
  { code: "JM", name: "Jamaica" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "HT", name: "Haiti" },
];

const POST_URL_RE =
  /\/(?:share|post)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export default function BroadcastPage() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [alertType, setAlertType] = useState<AlertType>("informational");
  const [scope, setScope] = useState<Scope>("app_wide");
  const [targetCountries, setTargetCountries] = useState<string[]>([]);
  const [articleUrl, setArticleUrl] = useState("");

  const [estimated, setEstimated] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<BroadcastRow[] | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api<{ broadcasts: BroadcastRow[] }>(
        "/news/admin/broadcasts?limit=20",
      );
      setHistory(r.broadcasts);
    } catch (e: any) {
      console.warn("Failed to load broadcast history:", e.message);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  function payload() {
    const post_id = (articleUrl.match(POST_URL_RE)?.[1] || "").trim() || undefined;
    return {
      title: title.trim(),
      body: body.trim(),
      alert_type: alertType,
      scope,
      target_countries:
        scope === "country_targeted" ? targetCountries : undefined,
      post_id,
    };
  }

  function validate(): string | null {
    if (!title.trim()) return "Title is required";
    if (!body.trim()) return "Body is required";
    if (body.length > 500) return "Body must be 500 characters or fewer";
    if (scope === "country_targeted" && targetCountries.length === 0) {
      return "Pick at least one country, or change scope to App-wide";
    }
    if (articleUrl.trim() && !POST_URL_RE.test(articleUrl)) {
      return "Article URL must include a valid post id";
    }
    return null;
  }

  async function previewRecipients() {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setEstimating(true);
    try {
      const r = await api<{ estimated_recipients: number }>(
        "/news/admin/broadcasts/preview",
        { method: "POST", body: payload() },
      );
      setEstimated(r.estimated_recipients);
    } catch (e: any) {
      setError(e.message ?? "Preview failed");
    } finally {
      setEstimating(false);
    }
  }

  async function send() {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    const confirmText =
      alertType === "breaking"
        ? "Send a BREAKING alert? This sends a push notification AND email to every targeted user. This cannot be undone."
        : "Send this broadcast? Push notifications will go out immediately.";
    if (!confirm(confirmText)) return;

    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ broadcast_id: string; recipients_count: number }>(
        "/news/admin/notifications/broadcast",
        { method: "POST", body: payload() },
      );
      setNotice(
        `Sent — delivered to ${r.recipients_count} recipients. (broadcast id: ${r.broadcast_id})`,
      );
      // Reset composer + reload history
      setTitle("");
      setBody("");
      setArticleUrl("");
      setEstimated(null);
      void loadHistory();
    } catch (e: any) {
      setError(e.message ?? "Send failed");
    } finally {
      setSending(false);
    }
  }

  function toggleCountry(code: string) {
    setTargetCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
    setEstimated(null);
  }

  const charsLeft = 500 - body.length;

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>
          Broadcast
        </h1>
        <CardSubtle>
          Send a push notification to all users or a specific country group.
        </CardSubtle>
      </header>

      <div style={styles.grid}>
        {/* LEFT: composer */}
        <Card style={{ marginBottom: 16 }}>
          <CardTitle>Compose</CardTitle>

          <Label style={{ marginTop: 12 }}>Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Short headline"
          />

          <Label style={{ marginTop: 12 }}>
            Body{" "}
            <span
              style={{
                color: charsLeft < 50 ? "var(--danger)" : "var(--text-muted)",
                fontWeight: 400,
              }}
            >
              ({charsLeft} chars left)
            </span>
          </Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="The push notification content."
          />

          <Label style={{ marginTop: 12 }}>Alert type</Label>
          <div style={styles.toggleRow}>
            <Button
              variant={alertType === "informational" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setAlertType("informational")}
            >
              Informational
            </Button>
            <Button
              variant={alertType === "breaking" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setAlertType("breaking")}
            >
              Breaking (also sends email)
            </Button>
          </div>

          <Label style={{ marginTop: 12 }}>Scope</Label>
          <Select
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as Scope);
              setEstimated(null);
            }}
          >
            <option value="app_wide">App-wide (all users)</option>
            <option value="country_targeted">Country-targeted</option>
          </Select>

          {scope === "country_targeted" && (
            <>
              <Label style={{ marginTop: 12 }}>Target countries</Label>
              <div style={styles.countryGrid}>
                {COUNTRIES.map((c) => (
                  <label key={c.code} style={styles.countryChip}>
                    <input
                      type="checkbox"
                      checked={targetCountries.includes(c.code)}
                      onChange={() => toggleCountry(c.code)}
                      style={{ marginRight: 6 }}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </>
          )}

          <Label style={{ marginTop: 12 }}>Linked article URL (optional)</Label>
          <Input
            value={articleUrl}
            onChange={(e) => setArticleUrl(e.target.value)}
            placeholder="https://globalvillage.app/share/<post-id>"
          />
        </Card>

        {/* RIGHT: preview + send */}
        <div>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Preview</CardTitle>
            <CardSubtle style={{ marginBottom: 12 }}>
              Estimate how many users will receive this broadcast.
            </CardSubtle>

            <Button
              variant="secondary"
              onClick={previewRecipients}
              disabled={estimating || sending}
            >
              {estimating ? (
                <>
                  <Spinner size={12} /> &nbsp; Estimating…
                </>
              ) : (
                "Preview recipient count"
              )}
            </Button>

            {estimated !== null && (
              <div style={styles.estimateBox}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  Estimated recipients
                </div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  {estimated.toLocaleString()}
                </div>
              </div>
            )}
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Send</CardTitle>
            <CardSubtle style={{ marginBottom: 12 }}>
              {alertType === "breaking"
                ? "Push + email. Use sparingly."
                : "Push only."}
            </CardSubtle>

            <Button
              variant={alertType === "breaking" ? "danger" : "primary"}
              onClick={send}
              disabled={sending}
            >
              {sending
                ? "Sending…"
                : alertType === "breaking"
                  ? "Send breaking alert"
                  : "Send broadcast"}
            </Button>

            {notice && (
              <div
                style={{
                  marginTop: 12,
                  color: "var(--success)",
                  fontSize: 13,
                }}
              >
                {notice}
              </div>
            )}
            {error && (
              <div
                style={{
                  marginTop: 12,
                  color: "var(--danger)",
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* History */}
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "32px 0 12px" }}>
        Recent broadcasts
      </h2>
      {history === null ? (
        <Spinner size={16} />
      ) : history.length === 0 ? (
        <Card>
          <CardSubtle>No broadcasts sent yet.</CardSubtle>
        </Card>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Scope</th>
                <th style={styles.th}>Recipients</th>
                <th style={styles.th}>Sent</th>
              </tr>
            </thead>
            <tbody>
              {history.map((b) => (
                <tr key={b.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong>{b.title}</strong>
                    <div style={styles.subline}>{truncate(b.body, 100)}</div>
                  </td>
                  <td style={styles.td}>{b.alert_type}</td>
                  <td style={styles.td}>
                    {b.scope === "app_wide"
                      ? "App-wide"
                      : `Countries: ${(b.target_countries ?? []).join(", ")}`}
                  </td>
                  <td style={styles.td}>{b.recipients_count ?? "—"}</td>
                  <td style={styles.td}>{formatRelative(b.sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function formatRelative(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  },
  toggleRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  countryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 6,
    maxHeight: 240,
    overflow: "auto",
    padding: 8,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg)",
  },
  countryChip: {
    fontSize: 13,
    color: "var(--text-secondary)",
    cursor: "pointer",
    padding: "2px 4px",
  },
  estimateBox: {
    marginTop: 16,
    padding: 12,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
  },
  tableWrap: {
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    background: "var(--surface)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-muted)",
    borderBottom: "1px solid var(--border)",
    background: "rgba(255,255,255,0.02)",
  },
  tr: {
    borderBottom: "1px solid var(--border)",
  },
  td: {
    padding: "12px 16px",
    fontSize: 14,
    color: "var(--text-primary)",
    verticalAlign: "top",
  },
  subline: {
    fontSize: 12,
    color: "var(--text-muted)",
    marginTop: 4,
  },
};
